import { ChevronDown, Clock } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export interface RecentsSectionProps {
  recents: string[];
  collapsed: boolean;
  sidebarCollapsed: boolean;
  onOpenChange: (open: boolean) => void;
  getTitle: (path: string) => string;
  isActive: (path: string) => boolean;
}

export function RecentsSection({
  recents,
  collapsed,
  sidebarCollapsed,
  onOpenChange,
  getTitle,
  isActive,
}: RecentsSectionProps) {
  if (recents.length === 0) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-[10px] uppercase tracking-widest text-sidebar-muted px-2">
        {!sidebarCollapsed && 'Recientes'}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <Collapsible open={!collapsed} onOpenChange={onOpenChange}>
          <CollapsibleTrigger
            className={cn(
              'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-sidebar-muted hover:bg-sidebar-accent transition-colors',
              sidebarCollapsed && 'justify-center',
            )}
            aria-label="Mostrar u ocultar recientes"
          >
            {!sidebarCollapsed && <span className="flex items-center gap-1.5"><Clock className="h-3 w-3" />Recientes</span>}
            {!sidebarCollapsed && (
              <ChevronDown
                className={cn('h-3 w-3 transition-transform', collapsed && '-rotate-90')}
              />
            )}
            {sidebarCollapsed && <Clock className="h-3.5 w-3.5" />}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenu>
              {recents.map((path) => (
                <SidebarMenuItem key={path}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={path}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors',
                        isActive(path) && 'bg-sidebar-accent text-sidebar-primary font-medium',
                      )}
                      activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                      title={getTitle(path)}
                    >
                      <Clock className="h-4 w-4 shrink-0" />
                      {!sidebarCollapsed && <span className="truncate">{getTitle(path)}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </CollapsibleContent>
        </Collapsible>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
